import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { recordValidStream } from "@/utils/streamProcessing";
import { showSponsorAd } from "@/stores/sponsorAdStore";
import { Track } from "@/types";
import { AppRole } from "@/types/supabase";

interface UseStreamProcessorProps {
  user: any;
  userRole: AppRole | null;
  track: Track;
  streamDuration: number;
  canProcess: boolean;
  onStreamRecorded?: () => void;
}

export function useStreamProcessor({
  user,
  userRole,
  track,
  streamDuration,
  canProcess,
  onStreamRecorded,
}: UseStreamProcessorProps) {
  const queryClient = useQueryClient();
  const [streamRecorded, setStreamRecorded] = useState(false);
  const [streamInProgress, setStreamInProgress] = useState(false);

  // Reset state when track changes
  useEffect(() => {
    console.log(
      "🔄 [STREAM_PROCESSOR] Resetting stream processor state for track:",
      track.id,
    );
    setStreamRecorded(false);
    setStreamInProgress(false);
  }, [track.id]);

  // Process stream when conditions are met
  useEffect(() => {
    const processStream = async () => {
      if (!user || !canProcess || streamRecorded || streamInProgress) {
        console.log("⏭️ [STREAM_PROCESSOR] Skipping stream processing:", {
          hasUser: !!user,
          canProcess,
          streamRecorded,
          streamInProgress,
          trackId: track.id,
        });
        return;
      }

      const minDuration = 30; // 30 seconds threshold for valid stream

      if (streamDuration >= minDuration) {
        try {
          console.log(
            `🎯 [STREAM_PROCESSOR] Stream validation threshold met for track ${track.id}:`,
            {
              streamDuration,
              minDuration,
              userRole,
              trackTitle: track.title,
              isSponsored: track.isSponsored,
              sponsorId: track.sponsorId,
              userId: user.id,
              artistId: track.artistId,
              timestamp: new Date().toISOString(),
            },
          );

          setStreamInProgress(true);
          setStreamRecorded(true);

          console.log("🚀 [STREAM_PROCESSOR] Calling recordValidStream...");
          const result = await recordValidStream(
            track.id,
            user.id,
            track.artistId,
            streamDuration,
            queryClient,
          );

          console.log("📊 [STREAM_PROCESSOR] Stream recording result:", {
            ...result,
            trackId: track.id,
            userId: user.id,
            artistId: track.artistId,
            timestamp: new Date().toISOString(),
          });

          if (result.success) {
            // Handle different user roles and stream types
            if (userRole === ("fan" as AppRole)) {
              if (result.counted) {
                console.log(
                  "✅ [STREAM_PROCESSOR] Stream was counted successfully",
                );

                // Check if this is a sponsored track and if payment was processed
                if (
                  track.isSponsored &&
                  result.sponsored &&
                  result.paymentProcessed
                ) {
                  // Show sponsor ad modal instead of toast
                  showSponsorAd({
                    sponsorId: track.sponsorId!,
                    trackTitle: track.title,
                  });
                  console.log(
                    "💰 [STREAM_PROCESSOR] Artist wallet credited with ₦50 for sponsored stream",
                  );
                } else if (
                  track.isSponsored &&
                  result.sponsored &&
                  !result.paymentProcessed
                ) {
                  // Stream was counted but payment failed
                  toast.warning(
                    "Stream counted, but sponsor payment is pending.",
                    {
                      duration: 4000,
                    },
                  );
                  console.error(
                    "❌ [STREAM_PROCESSOR] Artist wallet NOT credited - payment failed",
                  );
                } else if (track.isSponsored && !result.sponsored) {
                  // Stream was counted but not eligible for sponsored payment - silent log only
                  console.log(
                    `ℹ️ [STREAM_PROCESSOR] Stream not eligible for sponsored payment: ${result.eligibilityReason}`,
                  );
                  showSponsorAd({
                    sponsorId: track.sponsorId!,
                    trackTitle: track.title,
                  });
                } else {
                  // Regular non-sponsored stream
                  toast.success("Stream counted!");
                }
              } else {
                console.log(
                  "⚠️ [STREAM_PROCESSOR] Stream was recorded but not counted:",
                  result.eligibilityReason,
                );
                toast.info("Stream recorded, but this one wasn't counted.");
              }
            } else if (
              userRole === ("artist" as AppRole) &&
              track.artistId === user.id
            ) {
              console.log(
                "🎨 [STREAM_PROCESSOR] Artist streaming own track - not counted",
              );
              toast.info(
                "Artists cannot generate valid streams for their own tracks",
              );
            } else if (userRole !== ("fan" as AppRole)) {
              console.log(
                `👔 [STREAM_PROCESSOR] Stream by ${userRole} role - not counted`,
              );
              toast.info(`${userRole} accounts cannot generate valid streams`);
            }
          } else {
            console.error(
              "❌ [STREAM_PROCESSOR] Error recording stream:",
              result.error,
            );
            // Only show error toast for non-network errors
            if (
              result.error &&
              !result.error.includes("Failed to verify") &&
              !result.error.includes("Network")
            ) {
              toast.error(
                "We couldn't record that stream. Refresh and try again.",
              );
            } else {
              console.warn(
                "⚠️ [STREAM_PROCESSOR] Network error occurred, stream may not be tracked",
              );
            }
          }

          if (onStreamRecorded) {
            onStreamRecorded();
          }
        } catch (error) {
          console.error(
            "🔥 [STREAM_PROCESSOR] Exception in stream processing:",
            {
              error,
              errorMessage:
                error instanceof Error ? error.message : "Unknown error",
              trackId: track.id,
              userId: user.id,
              timestamp: new Date().toISOString(),
            },
          );
          toast.error(
            "An unexpected error occurred while processing your stream",
          );
        } finally {
          setStreamInProgress(false);
        }
      }
    };

    if (streamDuration > 0) {
      processStream();
    }
  }, [
    streamDuration,
    user,
    track,
    streamRecorded,
    streamInProgress,
    canProcess,
    queryClient,
    onStreamRecorded,
    userRole,
  ]);

  return {
    streamRecorded,
    streamInProgress,
  };
}
